import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TeacherJournal } from "types/teacherJournalTypes";
import { formatToLocaleDate } from "utils/dateUtils";
import { getSubjectLabelFromCode } from "utils/schoolUtils";
import { useAbility } from "config/abilityContext";
import NoDataComponent from "components/Common/NoData";
import Loader from "components/Common/loader";

import "./TeacherJournalFlipBook.css";
import { Modal } from "antd";
import CustomButton from "components/Common/ui/CustomButton";
interface Props {
  dataSource: TeacherJournal[];
  loading: boolean;
  onEdit: (record: TeacherJournal) => void;
  onDelete: (id: string) => void;
}
const { confirm } = Modal


type BookView = "cover" | "open" | "back";

const TeacherJournalFlipBook: React.FC<Props> = ({
  dataSource,
  loading,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation();
  const ability = useAbility();

  const journals = useMemo(() => dataSource || [], [dataSource]);
  const totalItems = journals.length;

  const [view, setView] = useState<BookView>("cover");
  const [spreadIndex, setSpreadIndex] = useState(0);

  const canEdit = ability.can("update", "TeacherJournal") || ability.can("read", "TeacherJournal");
  const canDelete = ability.can("delete", "TeacherJournal") || ability.can("read", "TeacherJournal");

  const spreadsCount = useMemo(() => Math.ceil(journals.length / 2), [journals.length]);


  useEffect(() => {
    if (!journals?.length) return;
    setView("open");
    setSpreadIndex(Math.max(0, Math.ceil(journals.length / 2) - 1));
  }, [journals]);


  if (loading) return <Loader />;
  if (!totalItems) return <NoDataComponent />;

  const firstJournal: any = journals?.[0];
  const coverClass = firstJournal?.class?.name || "—";
  const coverSubject =
    getSubjectLabelFromCode(firstJournal?.subject?.code) ||
    firstJournal?.subject?.name ||
    "—";
  const coverTeacher =
    firstJournal?.teacher?.fullName ||
    (firstJournal?.teacher?.user
      ? `${firstJournal.teacher.user.firstName} ${firstJournal.teacher.user.lastName}`
      : "—");

  const clampSpread = (n: number) => Math.max(0, Math.min(n, spreadsCount - 1));

  const goNext = () => {
    if (view === "cover") {
      setView("open");
      setSpreadIndex(0);
      return;
    }

    if (view === "open") {
      if (spreadIndex >= spreadsCount - 1) {
        setView("back");
      } else {
        setSpreadIndex((s) => clampSpread(s + 1));
      }
      return;
    }
  };

  const goPrev = () => {
    if (view === "back") {
      setView("open");
      setSpreadIndex(spreadsCount - 1);
      return;
    }

    if (view === "open") {
      if (spreadIndex <= 0) {
        setView("cover");
      } else {
        setSpreadIndex((s) => clampSpread(s - 1));
      }
      return;
    }
  };

  const showDeleteConfirm = (id: string) => {
    confirm({
      title: t("teacherJournal.delete_confirmation.title") as string,
      content: t("teacherJournal.delete_confirmation.description") as string,
      okText: t("delete") as string,
      okType: "danger",
      cancelText: t("cancel") as string,
      onOk: () => onDelete(id),
    });
  };


  const leftJournal = journals[spreadIndex * 2];
  const rightJournal = journals[spreadIndex * 2 + 1];

  const renderCoverClosed = () => (
    <div className="tj-closed">
      <div className="tj-closed-cover">
        <div className="tj-cover-badge">
          <span className="tj-icon"><i className="ti ti-notebook"></i></span>
          <span>{t("teacherJournal.flipbook.cover_title")}</span>
        </div>

        <div className="tj-cover-title">{t("teacherJournal.title")}</div>

        <div className="tj-cover-card">
          <div className="tj-cover-row">
            <span className="tj-k">{t("teacherJournal.form.class")}</span>
            <span className="tj-v">{coverClass}</span>
          </div>
          <div className="tj-cover-row">
            <span className="tj-k">{t("teacherJournal.form.subject")}</span>
            <span className="tj-v">{coverSubject}</span>
          </div>
          <div className="tj-cover-row">
            <span className="tj-k">{t("teacherJournal.table.teacher")}</span>
            <span className="tj-v">{coverTeacher}</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderBackClosed = () => (
    <div className="tj-closed">
      <div className="tj-closed-back">
        <div className="tj-back-title">{t("teacherJournal.back_cover.title")}</div>
        <div className="tj-back-sub">
          {t("teacherJournal.back_cover.pages", { count: totalItems })}
        </div>
        <div className="tj-back-quote">{t("teacherJournal.back_cover.quote")}</div>
      </div>
    </div>
  );

  const renderEntry = (journal: TeacherJournal, pageNumber: number) => {
    const j: any = journal;
    const className = j?.class?.name;
    const subjectName =
      getSubjectLabelFromCode(j?.subject?.code) || j?.subject?.name;
    const teacherLabel =
      j?.teacher?.fullName ||
      (j?.teacher?.user
        ? `${j.teacher.user.firstName} ${j.teacher.user.lastName}`
        : null);

    return (
      <div className="tj-page tj-entry">
        <div className="tj-top">
          <div className="tj-tags">
            {className && <span className="tj-tag tj-tag-class">{className}</span>}
            {subjectName && (
              <span className="tj-tag tj-tag-subject">{subjectName}</span>
            )}
            {teacherLabel && (
              <span className="tj-tag tj-tag-teacher">{teacherLabel}</span>
            )}
          </div>

          <div className="tj-meta">
            <div className="tj-meta-row">
              <span className="tj-muted"><i className="ti ti-calendar me-2"></i></span>
              <span>{formatToLocaleDate(journal.date)}</span>
            </div>

            {!!journal.sessionNumber && (
              <div className="tj-chip">
                {t("teacherJournal.form.session_number")}: #{journal.sessionNumber}
              </div>
            )}

            {!!journal.periodLabel && (
              <div className="tj-small">
                {t("teacherJournal.form.period")}: {journal.periodLabel}
              </div>
            )}
          </div>
        </div>

        <div className="tj-sep">
          <span className="dot" />
          <span className="line" />
          <span className="dot" />
        </div>

        <div className="tj-content">
          {journal.competency && (
            <div className="tj-block">
              <div className="tj-block-title">{t("teacherJournal.form.competency")}</div>
              <div className="tj-block-body">{journal.competency}</div>
            </div>
          )}

          {journal.objectives && (
            <div className="tj-block">
              <div className="tj-block-title">{t("teacherJournal.form.objectives")}</div>
              <div className="tj-block-body">{journal.objectives}</div>
            </div>
          )}

          <div className="row g-3">
            {journal.prerequisites && (
              <div className="col-12 col-lg-6">
                <div className="tj-grid-item">
                  <div className="tj-grid-label">{t("teacherJournal.form.prerequisites")}</div>
                  <div className="tj-grid-text">{journal.prerequisites}</div>
                </div>
              </div>
            )}

            {journal.materials && (
              <div className="col-12 col-lg-6">
                <div className="tj-grid-item">
                  <div className="tj-grid-label">{t("teacherJournal.form.materials")}</div>
                  <div className="tj-grid-text">{journal.materials}</div>
                </div>
              </div>
            )}

            {journal.methodology && (
              <div className="col-12 col-lg-6">
                <div className="tj-grid-item">
                  <div className="tj-grid-label">{t("teacherJournal.form.methodology")}</div>
                  <div className="tj-grid-text">{journal.methodology}</div>
                </div>
              </div>
            )}

            {journal.evaluation && (
              <div className="col-12 col-lg-6">
                <div className="tj-grid-item">
                  <div className="tj-grid-label">{t("teacherJournal.form.evaluation")}</div>
                  <div className="tj-grid-text">{journal.evaluation}</div>
                </div>
              </div>
            )}

            {journal.homework && (
              <div className="col-12 col-lg-6">
                <div className="tj-grid-item">
                  <div className="tj-grid-label">{t("teacherJournal.form.homework")}</div>
                  <div className="tj-grid-text">{journal.homework}</div>
                </div>
              </div>
            )}

            {journal.notes && (
              <div className="col-12">
                <div className="tj-grid-item">
                  <div className="tj-grid-label">{t("teacherJournal.form.notes")}</div>
                  <div className="tj-grid-text">{journal.notes}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="tj-actions">
          {canEdit && (
            <CustomButton onClick={() => onEdit(journal)} variant="primary" size="sm">
              <i className="ti ti-pencil me-2"></i>
              {t("edit")}
            </CustomButton>
          )}
          {canDelete && (
            <CustomButton onClick={() => showDeleteConfirm(journal.id)} variant="danger" size="sm">
              <i className="ti ti-trash me-2"></i>
              {t("delete")}
            </CustomButton>
          )}
        </div>

        <div className="tj-page-number">{pageNumber}</div>
      </div>
    );
  };

  const renderBlankPage = () => <div className="tj-page tj-blank" />;

  const renderOpenSpread = () => {
    const leftPageNumber = spreadIndex * 2 + 1;
    const rightPageNumber = spreadIndex * 2 + 2;

    return (
      <div className="tj-book-open">
        <div className="tj-spread">
          <div className="tj-paper tj-paper-left">
            <div className="tj-page-curl tj-page-curl-left" />
            {leftJournal ? renderEntry(leftJournal, leftPageNumber) : renderBlankPage()}
          </div>

          <div className="tj-gutter" />

          <div className="tj-paper tj-paper-right">
            <div className="tj-page-curl tj-page-curl-right" />
            {rightJournal ? renderEntry(rightJournal, rightPageNumber) : renderBlankPage()}
          </div>
        </div>
      </div>
    );
  };

  const canPrev = view !== "cover";
  const canNext = view !== "back";


  return (
    <div className="tj-shell container-fluid pb-2">
      {view === "cover" && renderCoverClosed()}
      {view === "open" && renderOpenSpread()}
      {view === "back" && renderBackClosed()}

      {/* NAV */}
      <div className="tj-nav d-flex justify-content-between align-items-center gap-2 flex-wrap mt-3">
        <button className="btn btn-light" type="button" onClick={goPrev} disabled={!canPrev}>
          ← {t("previous")}
        </button>

        <div className="tj-hint text-center flex-grow-1">
          {t("teacherJournal.flipbook.hint")} 
        </div>

        <button className="btn btn-light" type="button" onClick={goNext} disabled={!canNext}>
          {t("next")} →
        </button>
      </div>
    </div>
  );
};

export default TeacherJournalFlipBook;
